import { TableObject } from "./common/baseTableTypes";
import {
  BaseFullSchema_OLD,
  BaseCreateSchema,
  BaseUpdateSchema,
  PaginatedResponse,
  IdAsUUID,
} from "./common/commonTypes";
import { TypedSelectOption } from "./common/selectOptions";
import { InvMovBatchCreate } from "./invMovementTypes";

export const SHIPSTATION_RESIDENTIAL_INDICATORS = [
  "unknown",
  "yes",
  "no",
] as const;
export type ShipStationResidentialInidicator =
  (typeof SHIPSTATION_RESIDENTIAL_INDICATORS)[number];

export const SHIPMENT_REASONS = [
  "SO_FULFILLMENT",
  "PALLET_STERILIZE",
  "PALLET_RETURN",
  "TOOL_CALIBRATION",
  "CUSTOMER_LOAN",
  "CUSTOMER_LOAN_RETURN",
  "CUSTOMER_RMA",
  "CUSTOMER_RMA_FULFILL",
  "CONFERENCE_SUPPLY",
  "CONFERENCE_SUPPLY_RETURN",
  "OUTSOURCE_SUPPLY",
  "OUTSOURCE_BUILD",
  "PRODUCT_SAMPLE",
  "COMPONENT_SAMPLE",
  "RD_TESTING",
  "RD_TESTING_RETURN",
  "TRAINING_MATERIALS",
  "TRAINING_MATERIALS_RETURN",
  "FIELD_SERVICE",
  "FIELD_SERVICE_RETURN",
  "OTHER",
] as const;
export type ShipmentReason = (typeof SHIPMENT_REASONS)[number];

export const SHIPPING_CARRIERS = [
  "FEDEX",
  "UPS",
  "DHL",
  "LTL",
  "OTHER",
  "ANY",
  "NONE",
] as const;
export type ShippingCarrier = (typeof SHIPPING_CARRIERS)[number];

export const SHIPMENT_STATUSES = [
  "STAGING",
  "HOLDING",
  "LABEL_CREATED",
  "IN_TRANSIT",
  "DELIVERED",
  "ERROR",
  "VOID",
] as const;
export type ShipmentStatus = (typeof SHIPMENT_STATUSES)[number];

export interface ShippingDestination {
  nuc_entity_id: string;
  nuc_address_id: string;
  name: string;
  phone: string;
  address_line1: string;
  city_locality: string;
  state_province: string;
  postal_code: string;
  country_code: string;
  address_residential_indicator: ShipStationResidentialInidicator;
  email: string | null;
  company_name: string | null;
  address_line2: string | null;
  address_line3: string | null;
  instructions: string | null;
}

export type ShippingDestinationKey = keyof ShippingDestination;

export const DefaultShipDestination: ShippingDestination = {
  nuc_entity_id: "custom",
  nuc_address_id: "custom",
  name: "",
  phone: "999-999-9999",
  address_line1: "",
  city_locality: "",
  state_province: "",
  postal_code: "",
  country_code: "US",
  address_residential_indicator: "unknown",
  email: null,
  company_name: null,
  address_line2: null,
  address_line3: null,
  instructions: null,
};

export interface ShipmentRecordFull extends BaseFullSchema_OLD {
  name: string;

  status: ShipmentStatus;

  reason: ShipmentReason;
  reason_other: string | null;

  carrier: ShippingCarrier;
  carrier_other: string | null;
  method: string;

  shipment_id: string | null;
  shipment_system: string | null;

  associated_id: string | null;
  associated_table: string | null;

  ship_to: ShippingDestination;
  ship_from: ShippingDestination;

  primary_tracking: string | null;

  shipment_date: string | null;

  packages: ShipmentPackageInfoFull[];

  task_created: boolean | null;

  movement_batch_id: string | null;
}

export type PaginatedShipmentRecordResponse =
  PaginatedResponse<ShipmentRecordFull>;

export interface ShipmentRecordCreate extends BaseCreateSchema {
  name: string;

  status: ShipmentStatus | null;

  reason: ShipmentReason;
  reason_other: string | null;

  carrier: ShippingCarrier;
  carrier_other: string | null;
  method: string;

  shipment_id: string | null;
  shipment_system: string | null;

  associated_id: string | null;
  associated_table: string | null;

  ship_to: ShippingDestination;
  ship_from: ShippingDestination;

  primary_tracking: string | null;

  shipment_date: string | null;

  packages: ShipmentPackageInfoCreate[];

  movement: InvMovBatchCreate | null;
}

export interface ShipmentRecordUpdate extends BaseUpdateSchema {
  name?: string;

  status?: ShipmentStatus;

  reason?: ShipmentReason;
  reason_other?: string | null;

  carrier?: ShippingCarrier;
  carrier_other?: string | null;
  method?: string;

  shipment_id?: string | null;
  shipment_system?: string | null;

  associated_id?: string | null;
  associated_table?: string | null;

  ship_to?: ShippingDestination;
  ship_from?: ShippingDestination;

  primary_tracking?: string | null;

  shipment_date?: string;

  new_packages: ShipmentPackageInfoCreate[];
  edit_packages: ShipmentPackageInfoUpdate[];
  delete_packages: IdAsUUID[];
}

export interface ShipmentPackageInfoFull extends BaseFullSchema_OLD {
  shipment_record_id: string;

  status: ShipmentStatus;

  package_id: string | null;
  package_system: string | null;

  nuc_pkg_id: string;

  dimensions: TableObject<"pkg_dims">;
  weight: TableObject<"pkg_weight">;

  tracking: string | null;
  package_number: number;

  package_contents: string;
}

export interface ShipmentPackageInfoCreate extends BaseCreateSchema {
  status: ShipmentStatus | null;

  package_id: string | null;
  package_system: string | null;

  nuc_pkg_id: string;

  dimensions: TableObject<"pkg_dims">;
  weight: TableObject<"pkg_weight">;

  tracking: string | null;

  package_contents: string;
}

export interface ShipmentPackageInfoUpdate extends BaseUpdateSchema {
  status?: ShipmentStatus;

  package_id?: string | null;
  package_system?: string | null;

  nuc_pkg_id?: string;

  dimensions?: TableObject<"pkg_dims">;
  weight?: TableObject<"pkg_weight">;

  tracking?: string | null;

  package_contents?: string;
}

export type PackageOption = TypedSelectOption<
  "package",
  {
    catalog: string | null;
    weight: TableObject<"pkg_weight"> | null;
    dimensions: TableObject<"pkg_dims"> | null;
  }
>;
export const PackageOptions: PackageOption[] = [
  {
    label: "Custom Package",
    type: "package",
    value: {
      id: "custom",
      catalog: null,
      weight: null,
      dimensions: null,
    },
  },
  {
    label: "TGDK",
    type: "package",
    value: {
      id: "tgdk",
      catalog: "TGDK110012",
      weight: {
        value: 7.5,
        unit: "pound",
      },
      dimensions: {
        height: 12,
        length: 16,
        width: 16,
      },
    },
  },
  {
    label: "TGDK Box",
    type: "package",
    value: {
      id: "tgdkbox",
      catalog: null,
      weight: null,
      dimensions: {
        height: 12,
        length: 16,
        width: 16,
      },
    },
  },
  {
    label: "TGCT20",
    type: "package",
    value: {
      id: "tgct20",
      catalog: "TGCT120020",
      weight: {
        value: 4,
        unit: "pound",
      },
      dimensions: {
        height: 8,
        length: 12,
        width: 12,
      },
    },
  },
  {
    label: "TGCT28",
    type: "package",
    value: {
      id: "tgct28",
      catalog: "TGCT120028",
      weight: {
        value: 4.5,
        unit: "pound",
      },
      dimensions: {
        height: 8,
        length: 12,
        width: 12,
      },
    },
  },
  {
    label: "TGCT Box",
    type: "package",
    value: {
      id: "tgctbox",
      catalog: null,
      weight: null,
      dimensions: {
        height: 8,
        length: 12,
        width: 12,
      },
    },
  },
  {
    label: "TGCM",
    type: "package",
    value: {
      id: "tgcm",
      catalog: "TGCM1000",
      weight: {
        value: 7.5,
        unit: "pound",
      },
      dimensions: {
        height: 9,
        length: 17,
        width: 14,
      },
    },
  },
  {
    label: "TGCM Box",
    type: "package",
    value: {
      id: "tgcmbox",
      catalog: null,
      weight: null,
      dimensions: {
        height: 9,
        length: 17,
        width: 14,
      },
    },
  },
  {
    label: "TGPS",
    type: "package",
    value: {
      id: "tgps",
      catalog: "TGPS0100",
      weight: {
        value: 1.5,
        unit: "pound",
      },
      dimensions: {
        height: 2,
        length: 9,
        width: 6,
      },
    },
  },
  {
    label: "White Mailer Box",
    type: "package",
    value: {
      id: "whitemailer",
      catalog: null,
      weight: null,
      dimensions: {
        height: 2,
        length: 9,
        width: 6,
      },
    },
  },
];
